import { getClientProductNumber } from 'utils/getProductNumber';
import { getFetchHeaders } from 'utils/fetchHeaders';
import { isInWebView } from 'utils/isInWebView';
import { postMessageToApp } from 'utils/postMessageToApp';
import { useErrorManager } from 'InternalLibrary';
import { useGetLegalTerms } from 'api';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useClient from './useClient';

export interface LegalTerms {
  getLegalTerms: (amount: string, term: number) => Promise<string>;
  printLegalTerms: (contentHtml: string) => void;
}

export const useLegalTerms = (): LegalTerms => {
  const { t } = useTranslation('legalTermsModal');
  const apiLegalTerms = useGetLegalTerms();
  // const apiLegalTermsPdf = useGetLegalTermsPDF(); Disabled for now - MAD1-4600
  const { client } = useClient();
  const { setError } = useErrorManager();

  const getLegalTerms = async (amount: string, term: number) => {
    try {
      // TODO: solve type - in this corner case, the response is a string (obsolete react library api)
      // @ts-ignore
      const data: string = await apiLegalTerms.fetch({
        headers: getFetchHeaders({
          Accept: 'text/html',
          'Content-Type': 'text/html',
        }),
        queryParameters: {
          attachment: false,
          amount,
          term,
          productNumber: getClientProductNumber(client?.availableProductsInfo),
        },
      });
      return data;
    } catch (error) {
      setError(error);
      return '';
    }
  };

  /* Disabled for now - MAD1-4600
  const handleLegalContentPDF = async (amount: string, term: number) => {
    try {
      const data = await apiLegalTermsPdf.fetch({
        headers: getFetchHeaders({
          Accept: 'application/pdf',
          'Content-Type': 'application/pdf',
        }),
        queryParameters: {
          attachment: false,
          amount,
          term,
          productNumber: client?.productNumber || App.config.productNumber,
        },
      });
      return data;
    } catch (e) {
      setError(e);
    }
  };*/

  const printLegalTerms = (contentHtml: string) => {
    // Print dialog on WEB app
    if (isInWebView()) {
      postMessageToApp('download_pdf', { content: contentHtml });
      return;
    }

    // Print dialog on WEB app
    const printWindow = window.open('', '_blank', 'width=800,height=800');

    if (!printWindow) return;

    printWindow.document.write(
      `<html><head><title>${t('legal_terms')}</title></head><body>`,
    );
    printWindow.document.write(contentHtml);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();

    return true;
  };

  const result = useMemo<LegalTerms>(
    () => ({
      getLegalTerms,
      printLegalTerms,
    }),
    [getLegalTerms, printLegalTerms],
  );

  return result;
};
